import { useMemo } from "react";

import {
  faExclamationCircle,
  faLock,
  faMagnifyingGlass,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { twJoin } from "tailwind-merge";

import { DisplayDateFromNow } from "src/base-components/Date";
import { Cell, Header, TableComp } from "src/base-components/Table";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { CaseStatusPillWithSchema } from "src/cases/common/CaseStatusPillWithSchema";
import { UserPill } from "src/cases/common/UserPill";
import { useCasesForEntity } from "src/cases/queries/cases";
import { CasesDeserialized } from "src/cases/queries/casesTypes";
import { useCaseQueues } from "src/cases/queries/queues";
import { GetCasesByEntityApiV1CasesRelatedGetFilterEnvEnum } from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { useSidepane } from "src/entities/entityView/utils";
import { useEnvironment } from "src/hooks/useEnvironment";
import { EntityViewParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type EntityCase = CasesDeserialized["data"][number];

const helper = createColumnHelper<EntityCase>();

const buildColumns = (queueNameById: Record<string, string>) => [
  helper.accessor("id", {
    id: "id",
    header: (info) => (
      <Header dataLoc="entity-cases-header-id" info={info}>
        <span className="whitespace-nowrap">Case ID</span>
      </Header>
    ),
    cell: (info) => (
      <Cell className="w-24" info={info}>
        <TruncatedIdPill id={info.getValue()} variant="last-five" />
      </Cell>
    ),
  }),
  helper.accessor((row) => row.queue?.id, {
    id: "queue",
    header: (info) => (
      <Header dataLoc="entity-cases-header-queue" info={info}>
        Case Queue
      </Header>
    ),
    cell: (info) => {
      const queueId = info.getValue();
      return (
        <Cell info={info}>{queueId ? (queueNameById[queueId] ?? "") : ""}</Cell>
      );
    },
  }),
  helper.accessor((row) => row, {
    id: "status",
    header: (info) => (
      <Header dataLoc="entity-cases-header-status" info={info}>
        Status
      </Header>
    ),
    cell: (info) => {
      const { case_properties, template, template_version } = info.row.original;

      return (
        <Cell info={info}>
          <CaseStatusPillWithSchema
            status={case_properties.status}
            subStatus={case_properties.sub_status}
            templateId={template.id}
            versionId={template_version.id}
          />
        </Cell>
      );
    },
  }),
  helper.accessor((row) => row.assignee?.id, {
    id: "assignee",
    header: (info) => (
      <Header dataLoc="entity-cases-header-assignee" info={info}>
        Assigned to
      </Header>
    ),
    cell: (info) => {
      const assigneeId = info.getValue();
      return (
        <Cell info={info}>
          {assigneeId ? <UserPill userId={assigneeId} /> : null}
        </Cell>
      );
    },
  }),
  helper.accessor("created_at", {
    id: "created_at",
    header: (info) => (
      <Header dataLoc="entity-cases-header-created-at" info={info}>
        Created at
      </Header>
    ),
    cell: (info) => (
      <Cell className="font-inter-normal-12px" info={info}>
        <DisplayDateFromNow value={info.getValue()} />
      </Cell>
    ),
  }),
];

type EntityCasesProps = {
  entityId: string;
};

export const EntityCases: React.FC<EntityCasesProps> = ({ entityId }) => {
  const { schema } = useParamsDecode<EntityViewParams>();
  const [environment] = useEnvironment();

  const {
    data: cases,
    isLoading,
    isError,
    error,
    fetchNextPage,
    isFetchingNextPage,
  } = useCasesForEntity({
    entityId,
    entityType: schema,
    environment:
      environment as GetCasesByEntityApiV1CasesRelatedGetFilterEnvEnum,
  });

  const isForbidden = error?.response?.status === 403;

  const { resourceId: selectedCaseId, toggleSidepane } = useSidepane("case");

  const { data: queues } = useCaseQueues();
  const queueNameById = useMemo(
    () =>
      Object.fromEntries((queues ?? []).map((queue) => [queue.id, queue.name])),
    [queues],
  );

  const columns = useMemo(() => buildColumns(queueNameById), [queueNameById]);

  const hasCases = !isLoading && !!cases?.length;
  const isEmpty = !isLoading && !isError && !hasCases;

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white p-4">
      <TableComp
        columns={columns}
        data={cases ?? []}
        dataLoc="entity-cases-table"
        frameClassName={twJoin("w-full", (isEmpty || isError) && "h-auto")}
        getRowId={(row) => row.id}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading}
        rowPropsGetter={(row) => ({
          "data-loc": `entity-case-row-${row.id}`,
          onClick: () => toggleSidepane({ id: row.id }),
          classNameOverrides: twJoin(
            "cursor-pointer",
            row.id === selectedCaseId
              ? "selected bg-gray-50"
              : "hover:bg-gray-50",
          ),
        })}
        variant="compact"
        onScrolledToTheBottom={fetchNextPage}
      />
      {isError && (
        <div className="h-96">
          <EmptyState
            dataLoc="entity-cases-error-state"
            description={
              isForbidden
                ? "You don't have permission to view Cases for this Entity"
                : "We're having trouble loading Cases. Try refreshing the page"
            }
            headline={isForbidden ? "Access restricted" : "Error loading Cases"}
            icon={isForbidden ? faLock : faExclamationCircle}
            variant="error"
          />
        </div>
      )}
      {isEmpty && (
        <div className="h-96" data-loc="entity-cases-empty-state">
          <EmptyState
            description="There are no Cases related to this Entity yet"
            headline="No Cases found"
            icon={faMagnifyingGlass}
          />
        </div>
      )}
    </div>
  );
};

export const CasesTabContent: React.FC = () => {
  const { id } = useParamsDecode<EntityViewParams>();

  return (
    <div className="p-5">
      <EntityCases entityId={id} />
    </div>
  );
};
